import { rollup, sum } from "d3-array";
import { format } from "d3-format";
import { scaleLinear } from "d3-scale";
import { tuple } from "fp-ts/lib/function";
import { pipe } from "fp-ts/lib/pipeable";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { removeTrailingZero } from "../charts/lib/utils";
import { StackedBarAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_fig_GOVHH_ABS_REL";
import { datasets } from "../data/data_finance_fig_GOVHH_ABS_REL_datasets";
import { countryIdIso, getCountryMeta, mkGemDataDecoder, useButtonGroupState, useFigureState, useNamedCountries } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, identity, io, O, Ord } from "../prelude";
export * from "../data/data_finance_fig_GOVHH_ABS_REL";
const Indicator = io.keyof({
    FSGOV: null,
    FSHH: null
});
const indicatorLookup = {
    FSGOV: defineMessage({
        id: "fig.finance.GOVHH_ABS_REL.FSGOV",
        defaultMessage: "Government funding of education as a percentage of GDP"
    }),
    FSHH: defineMessage({
        id: "fig.finance.GOVHH_ABS_REL.FSHH",
        defaultMessage: "Household funding of education as a percentage of GDP"
    })
};
const indicatorLookupRel = {
    FSGOV: defineMessage({
        id: "fig.finance.GOVHH_ABS_REL.FSGOV_rel",
        defaultMessage: "Share of government in total education expenditure"
    }),
    FSHH: defineMessage({
        id: "fig.finance.GOVHH_ABS_REL.FSHH_rel",
        defaultMessage: "Share of households in total education expenditure"
    })
};
export const Data = mkGemDataDecoder([
    "iso3c",
    "year"
], {
    ind_id: Indicator,
    value: io.number
});
export const Chart = ({ data: rawData })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const env = useConfig();
    const [, actions] = useFigureState();
    const [selectedUnit] = useButtonGroupState("unit");
    const { activeSection } = useReadScrolly();
    const [scrollyUnit, scrollyCountries] = React.useMemo(()=>pipe(activeSection, O.map((x)=>x.split(",")), O.fold(()=>tuple("absolute", O.none), (strIds)=>{
            const [unit, ...countryIds] = strIds; // Expected format: "relative,KHM,TJK,UGA"
            const scrollyUnit = unit === "relative" ? "relative" : "absolute";
            return tuple(scrollyUnit, pipe(Ar.array.sequence(O.option)(countryIds.map((mbId)=>getCountryMeta(env.countries, countryIdIso.wrap(mbId)))), O.map((xs)=>xs.map((meta)=>meta.country)), O.chain(An.fromArray)));
        })), [
        env.countries,
        activeSection
    ]);
    const data = useNamedCountries(rawData);
    React.useEffect(()=>{
        actions.setSelectionControl("unit", {
            type: "ButtonGroup",
            selected: "absolute"
        }, [
            {
                value: "absolute",
                label: intl.formatMessage(defineMessage({
                    id: "Percent of GDP",
                    defaultMessage: "Percent of GDP"
                }))
            },
            {
                value: "relative",
                label: intl.formatMessage(defineMessage({
                    id: "Ratio",
                    defaultMessage: "Ratio"
                }))
            }
        ]);
    }, [
        intl,
        actions
    ]);
    React.useEffect(()=>{
        if (O.isSome(activeSection)) {
            actions.updateSelectionControl("unit", scrollyUnit);
        }
    }, [
        intl,
        actions,
        activeSection,
        scrollyUnit
    ]);
    const isAbsolute = selectedUnit === "absolute";
    const stretchedScale = React.useMemo(()=>{
        const scales = rollup(data, (xs)=>scaleLinear().domain([
                0,
                sum(xs, (x)=>x.value)
            ]).range([
                0,
                1
            ]), (x)=>x.iso3c);
        return (country, value)=>scales.get(country)(value);
    }, [
        data
    ]);
    const getCountryTotal = React.useMemo(()=>{
        const lookup = rollup(data, (xs)=>xs.reduce((sum, x)=>sum + x.value, 0), (x)=>x.iso3c);
        return (id)=>lookup.get(id);
    }, [
        data
    ]);
    const getHouseholdTotal = React.useMemo(()=>{
        const lookup = rollup(data, (xs)=>{
            const el = xs.find((x)=>x.ind_id === "FSHH");
            return el != null ? stretchedScale(el.iso3c, el.value) : 0;
        }, (x)=>x.iso3c);
        return (id)=>lookup.get(id);
    }, [
        data,
        stretchedScale
    ]);
    const chartData = React.useMemo(()=>{
        return pipe(data, Ar.map((x)=>isAbsolute ? x : {
                ...x,
                value: stretchedScale(x.iso3c, x.value)
            }), Ar.sortBy([
            Ord.contramap((x)=>x.ind_id === "FSGOV" ? 0 : 1)(Ord.ordNumber),
            Ord.contramap((x)=>isAbsolute ? -getCountryTotal(x.iso3c) : -getHouseholdTotal(x.iso3c))(Ord.ordNumber)
        ]));
    }, [
        isAbsolute,
        data,
        stretchedScale,
        getCountryTotal,
        getHouseholdTotal
    ]);
    const getStack = React.useCallback((x)=>x.country_name, []);
    const getLayer = React.useCallback((x)=>x.ind_id, []);
    const getLayerLabel = React.useCallback((ind_id)=>isAbsolute ? intl.formatMessage(indicatorLookup[ind_id]) : intl.formatMessage(indicatorLookupRel[ind_id]), [
        intl,
        isAbsolute
    ]);
    const getValue = React.useCallback((x)=>x.value || 0, []);
    const formatY = React.useMemo(()=>removeTrailingZero(format(isAbsolute ? ".1%" : ".0%")), [
        isAbsolute
    ]);
    const formatYAxisLabel = React.useMemo(()=>format(".0%"), []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    return pipe(An.fromArray(chartData), O.map((xs)=><StackedBarAuto key={"linter-map"} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={xs} getStack={getStack} getLayer={getLayer} getLayerLabel={getLayerLabel} getValue={getValue} formatX={identity} formatY={formatY} formatYAxisLabel={formatYAxisLabel} colorPalette={getColorPalette} showTooltip hideXAxisLabel highlightedItems={scrollyCountries}/>), O.toNullable);
};
export default withFigureIO({
    url: new URL("../data/data_finance_fig_GOVHH_ABS_REL.json", import.meta.url).href,
    csv: new URL("../data/data_finance_fig_GOVHH_ABS_REL.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_fig_GOVHH_ABS_REL.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
